import moment from 'moment';
import { isEmail, isPhone } from '@services/utils/validators';
import SimpleTrack from '@common/types/release/simple-track';
import { ContactInfo } from '@webapp/types/common/contact-info';
import { SimpleRelease } from '@common/types/release/simple-release';
import { ReleaseGenre } from '@common/types/release/release-genre';
import { ReleaseStore } from '@common/types/release/release-store';
import { ReleaseRegion } from '@common/types/release/release-region';
import AppState from '@webapp/store/state';
import { collaboratorsList } from '@webapp/store/collaborator/selectors';

interface TreeElement {
  name: string;
  children: ReleaseRegion[];
}

export const normalizeEmail = (text: string) => {
  if (isEmail(text)) {
    return text.toLowerCase();
  }
};

export const normalizePhone = (text: string) => {
  if (isPhone(text)) {
    return text.replace(/-/g, '').replace(/\s/g, '').replace(/[()]/g, '');
  }
};

export const normalizePhoneOrEmail = (text: string): ContactInfo | void => {
  if (isEmail(text)) {
    return {
      value: normalizeEmail(text) as string,
      type: 'email',
    };
  }

  if (isPhone(text)) {
    return {
      value: normalizePhone(text) as string,
      type: 'sms',
    };
  }
};

export const normalizeTitle = (text: string) =>
  text
    .replace(/\s{2,}/g, ' ')
    .split(' ')
    .map((part) =>
      part.length >= 1 ? `${part[0].toLocaleUpperCase()}${part.slice(1).toLocaleLowerCase()}` : ''
    )
    .join(' ');

export const joinList = (l: string[]): string => {
  if (l.length <= 1) {
    return l.join();
  }

  const begining = l.slice(0, -1);
  const end = l.slice(-1)[0];

  return `${begining.join(', ')} & ${end}`;
};

export const normalizeTrackName = (
  track: Partial<SimpleTrack>,
  state: AppState
) => {
  if (track && track.title) {
    let name = track.title;

    if (track.id) {
      const featuring = collaboratorsList(state.collaborator, track.id)
        .filter((a) => a.role === 'featuring')
        .map((a) => a.name || a.contact || '');

      if (featuring.length) {
        name = `${name} (feat. ${joinList(featuring)})`;
      }
    }

    if (track.trackVersion?.trim()) {
      name = `${name} [${normalizeTitle(track.trackVersion.trim())}]`;
    }

    if (
      state.release.release?.type === 'single' &&
      state.release.release.version?.trim()
    ) {
      name = `${name} [${normalizeTitle(
        state.release.release.version.trim()
      )}]`;
    }

    return name;
  }

  return '?';
};

export const formatOwners = (ownerCount?: number) => {
  if (!ownerCount) {
    return '-';
  }

  return `${ownerCount} Owner${ownerCount !== 1 ? 's' : ''}`;
};

export const formatDate = (release: SimpleRelease) => {
  const date =
    release.releaseHistory === 'new'
      ? release.releaseDate
      : release.previousReleaseDate;

  return moment(date).format('ll');
};

export const formatGenre = (genres: ReleaseGenre[]) => {
  return joinList(genres.map((g) => g.value) || []);
};

const getBrief = (list: string[], size = 3) => {
  const highlighted = list.sort().slice(0, size);
  let result = `${highlighted.join(', ')}`;

  if (list.length > size) {
    result = `${result} and ${list.length - size} other${
      list.length > size + 1 ? 's' : ''
    }`;
  }
  return result;
};

export const formatRegions = (
  regions: ReleaseRegion[],
  continents: TreeElement[] = []
) => {
  return getBrief(
    regions.map((r) => r.name).concat(continents.map((r) => r.name)),
    2
  );
};

export const formatStores = (active: ReleaseStore[], all: string[]) => {
  if (!active.length) {
    return 'Select at least one store';
  }

  const stores = active.length ? active.map((s) => s.name) : all;

  if (stores.length === all.length) {
    return `All Stores (${stores.length})`;
  }

  return getBrief(stores);
};

export const centsToDollars = (cents: number): string => {
  const numbs = `${Math.round(cents) / 100}`.split('.');
  if (numbs.length < 2) return `${(cents / 100).toFixed(2)}`;
  if (numbs[1].length < 2) {
    numbs[1] = numbs[1] + '0';
  }
  return numbs.join('.');
};
